import { ref, watch, onMounted } from 'vue';
import { useUserStore } from '@/stores/UserStore';
import useSettingsGlobals from '@/composables/useSettingsGlobals'

const { $PLATFORM, $WEB_VIEW } = useSettingsGlobals()

const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);

export default (initialValue, key, system = 'localStorage') => {
  if (system === 'off') return;

  const storageSystem = ref(system);
  const userStore = useUserStore();

  const returnValue = ref(initialValue)
  const loaded = ref(false)
  let storedValue;
  let response;
  let unwatch;

  // Parse the raw stored response into returnValue and register the
  // persistence watch. Called at different times per storage system (see
  // below), so consumers should gate on `loaded` when they need the real
  // stored value rather than the initial one.
  function hydrate() {
    storedValue = response ? response : initialValue;

    if (storedValue !== null && storedValue !== 'undefined') {
      try {
        returnValue.value = JSON.parse(storedValue)
      } catch (err) {
        if (err instanceof SyntaxError) {
          returnValue.value = storedValue // treat as plain string
        } else {
          throw err
        }
      } finally {
        loaded.value = true
      }
    }

    unwatch = watch(
      returnValue,
      (val) => {
        if (storageSystem.value === 'localStorage' && !$WEB_VIEW) {
          try {
            window.localStorage.setItem(key, JSON.stringify(val))
          } catch (err) {
            // storage blocked (Safari private mode / cookies disabled) or quota
            // exceeded — nothing we can persist, so skip rather than throw.
          }
        }

        if (storageSystem.value === 'webViewMessenger') {
          if ($PLATFORM === 'android') {
            const params = [key, val.toString()]

            alert(params.join(','))
          } else {
            const info = {
              type: key,
              value: val
            }

            window.webkit.messageHandlers.observe.postMessage(info)
          }
        }

        if (storageSystem.value === 'apiUserStorage') {
          userStore.updateAPIStorageItem(key, JSON.stringify(val))
        }

      },
      {
        deep: true
      }
    )
  }

  if (storageSystem.value === 'localStorage') {
    // localStorage is synchronous — hydrate immediately so consumers that read
    // the value during setup (store fetches, initial renders) get the saved
    // value instead of the default. Hydrating in onMounted let the favorites
    // page fetch with the wrong sort order before the saved value arrived
    // (LIF-99/LIF-100). Webviews intentionally skip localStorage and keep the
    // initial value.
    if (!$WEB_VIEW) {
      try {
        response = window.localStorage.getItem(key)
      } catch (err) {
        // localStorage can be blocked (Safari private mode / cookies
        // disabled), throwing SecurityError. Fall back to the initial value.
        response = null
      }
    }

    hydrate()
  } else if (storageSystem.value === 'apiUserStorage') {
    // An API fetch has no DOM dependency, so kick it off immediately rather
    // than deferring to onMounted. Waiting for mount let the `loaded` flag
    // resolve after consumers had already acted on the initial value (e.g. the
    // tide store picking a default spot before the saved one loaded — LIF-103).
    (async () => {
      response = await userStore.getAPIStorageItem(key);

      hydrate()
    })()
  } else {
    // webViewMessenger keeps its original mount timing.
    onMounted(() => {
      response = urlParams.get(key);

      hydrate()
    })
  }

  function updateStorageSystem(newSystem) {
    storageSystem.value = newSystem;
  }

  return [returnValue, loaded, unwatch, updateStorageSystem];
}
